import type { SrtEntry, WordEntry } from '@lambda-libs/types';

interface EditableStore {
  sourceId: string | null;
  srtData: SrtEntry[];
  updateEntry(entry: SrtEntry, before?: SrtEntry, expectedSourceId?: string | null): Promise<unknown> | unknown;
  markPendingEdit?(): void;
}
interface PendingEdit {
  sourceId: string | null;
  id: string;
  text?: string;
  timing?: { startTime: number; endTime: number };
  timingBefore?: SrtEntry;
  timer?: ReturnType<typeof setTimeout>;
  waiters: Array<{ resolve(value: boolean): void; reject(error: unknown): void }>;
}
interface EditQueue {
  store: EditableStore;
  pending: Map<string, PendingEdit>;
  running: Promise<void>;
  inFlight: number;
  lastError?: unknown;
}
const queues = new WeakMap<EditableStore, EditQueue>();
const active = new Set<EditQueue>();
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const tokens = (text: string) => text.replace(/\r\n?/g, '\n').match(/\n|[^\s]+/g) || [];

// Keep the original word timings when only whitespace changes. When words or
// cue bounds change, distribute their timings within the exact cue bounds.
export function wordsForText(text: string, start: number, end: number): WordEntry[] {
  const parts = tokens(text), length = parts.reduce((sum, word) => sum + (word === '\n' ? 0 : word.length), 0);
  if (!length) return [];
  let position = 0;
  return parts.map(word => {
    const from = start + (end - start) * position / length;
    if (word !== '\n') position += word.length;
    return { word, start: from, end: start + (end - start) * position / length };
  });
}
function queueFor(store: EditableStore) {
  let queue = queues.get(store);
  if (!queue) {
    queue = { store, pending: new Map(), running: Promise.resolve(), inFlight: 0 };
    queues.set(store, queue);
  }
  return queue;
}
function processEdit(queue: EditQueue, id: string) {
  const edit = queue.pending.get(id);
  if (!edit) return;
  queue.pending.delete(id);
  if (edit.timer) clearTimeout(edit.timer);
  edit.timer = undefined;
  queue.inFlight++;
  queue.running = queue.running.then(async () => {
    // An old timer must never write to a newly opened document or recreate a
    // deleted cue. Normal source transitions flush before changing identity.
    const current = queue.store.srtData.find(entry => entry.id === edit.id);
    if (queue.store.sourceId !== edit.sourceId || !current) {
      edit.waiters.forEach(waiter => waiter.resolve(false)); return;
    }
    const before = copy(current);
    if (edit.timingBefore) {
      before.startTime = edit.timingBefore.startTime;
      before.endTime = edit.timingBefore.endTime;
      before.words = before.text === edit.timingBefore.text ? copy(edit.timingBefore.words || [])
        : wordsForText(before.text, before.startTime, before.endTime);
    }
    const next = { ...copy(current), ...edit.timing, ...(edit.text !== undefined ? { text: edit.text } : {}) };
    if (!Number.isFinite(next.startTime) || !Number.isFinite(next.endTime) || next.startTime < 0 || next.endTime < next.startTime) {
      throw new Error('Invalid subtitle timing');
    }
    next.lines = next.text.split('\n').length;
    const sameWords = JSON.stringify(tokens(next.text)) === JSON.stringify((before.words || []).map(word => word.word));
    if (!sameWords || before.startTime !== next.startTime || before.endTime !== next.endTime) next.words = wordsForText(next.text, next.startTime, next.endTime);
    // Pass the source the edit was read from: when a source replacement won
    // the race, the store drops the write (returning false) instead of
    // applying stale rows to the new document.
    if (JSON.stringify(before) !== JSON.stringify(next)) {
      const applied = await queue.store.updateEntry(next, before, edit.sourceId);
      edit.waiters.forEach(waiter => waiter.resolve(applied !== false));
    } else edit.waiters.forEach(waiter => waiter.resolve(true));
  }).catch(error => {
    edit.waiters.forEach(waiter => waiter.reject(error)); edit.waiters = [];
    if (queue.store.sourceId !== edit.sourceId) return;
    queue.lastError = error;
    // Retain failed edits for an explicit flush/retry, without an endless
    // automatic retry loop. A newer pending edit keeps its newer values.
    const newer = queue.pending.get(id);
    if (newer) {
      if (newer.text === undefined) newer.text = edit.text;
      if (!newer.timing) newer.timing = edit.timing;
      newer.timingBefore = edit.timingBefore || newer.timingBefore;
    } else queue.pending.set(id, edit);
  }).finally(() => {
    queue.inFlight--;
    if (!queue.pending.size && !queue.inFlight) active.delete(queue);
  });
}

// True while debounced (not yet applied) or in-flight edits exist for the
// given store (all stores when omitted). Lets unload guards ask whether any
// typed work is still only in memory, without flushing.
export function hasPendingSubtitleUpdates(store?: EditableStore): boolean {
  for (const queue of active) {
    if (store && queue.store !== store) continue;
    if (queue.pending.size > 0 || queue.inFlight > 0) return true;
  }
  return false;
}

export async function flushPendingSubtitleUpdates(store?: EditableStore): Promise<void> {
  for (const queue of [...active]) {
    if (store && queue.store !== store) continue;
    queue.lastError = undefined;
    do {
      for (const id of [...queue.pending.keys()]) processEdit(queue, id);
      await queue.running;
      if (queue.lastError) throw queue.lastError;
    } while (queue.pending.size || queue.inFlight);
  }
}

// Text and timing controls share a queue for the same store. Debouncing one cue
// never cancels another cue, and saves await both queued and in-flight updates.
export function createDebouncedUpdate() {
  return {
    queueUpdate(item: SrtEntry, text: string | undefined, store: EditableStore, before?: SrtEntry): Promise<boolean> {
      const queue = queueFor(store);
      let edit = queue.pending.get(item.id);
      if (edit && edit.sourceId !== store.sourceId) {
        if (edit.timer) clearTimeout(edit.timer);
        edit.waiters.forEach(waiter => waiter.resolve(false)); queue.pending.delete(item.id); edit = undefined;
      }
      if (!edit) { edit = { id: item.id, sourceId: store.sourceId, waiters: [] }; queue.pending.set(item.id, edit); }
      if (text !== undefined) edit.text = text;
      else {
        edit.timing = { startTime: item.startTime, endTime: item.endTime };
        edit.timingBefore ||= copy(before || store.srtData.find(entry => entry.id === item.id) || item);
      }
      if (edit.timer) clearTimeout(edit.timer);
      edit.timer = setTimeout(() => processEdit(queue, item.id), 200);
      active.add(queue); store.markPendingEdit?.();
      return new Promise((resolve, reject) => edit!.waiters.push({ resolve, reject }));
    },
    forceUpdate: (store?: EditableStore) => flushPendingSubtitleUpdates(store),
  };
}
